import testingService from '@/services/testingService';
import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest, select } from 'redux-saga/effects';
import {
    fetchCreateSurveyRequest,
    fetchCreateSurveySuccess,
    fetchCreateSurveyFailure,
    fetchStartTestingRequest,
    fetchStartTestingSuccess,
    fetchStartTestingFailure,
    fetchSubmitTestingRequest,
    fetchSubmitTestingSuccess,
    fetchSubmitTestingFailure,
    fetchSubmitQuestionTestingRequest,
    fetchSubmitQuestionTestingSuccess,
    fetchSubmitQuestionTestingFailure,
    setCurrentQuestion,
    clearSurvey,
    fetchResultTestingSuccess,
    fetchResultTestingFailure,
    fetchResultTestingRequest,
    fetchHistoryListRequest,
    fetchHistoryListSuccess,
    fetchHistoryListFailure,
    fetchHistoryDetailsRequest,
    fetchHistoryDetailsSuccess,
    fetchHistoryDetailsFailure,
    fetchCreateTestingLessonRequest,
    fetchCreateTestingLessonSuccess,
    fetchCreateTestingLessonFailure,
} from '../slices/testingSlice';
import {
    IStartTesting,
    ISubmitQuestionTesting,
    ISubmitTesting,
    ISurveyUser,
} from '@/features/landing/interface';
import { AppEvent, emitter } from '@/plugin/mitt';
import { SEVERITY_NOTIFICATION } from '@/common/constants';
import { setIsInitialAssessmentCompleted } from '../slices/authSlice';
import { IGetListAssessmentParams } from '@/features/student/Assessments/interface';

function* createSurveyWorker(action: PayloadAction<ISurveyUser>): SagaIterator {
    try {
        const response = yield call(testingService.createSurvey, action.payload);
        yield put(fetchCreateSurveySuccess(response));
    } catch (error: any) {
        yield put(fetchCreateSurveyFailure(error.response?.data?.message || 'Create survey failed'));
    }
}

function* startTestingWorker(action: PayloadAction<IStartTesting>): SagaIterator {
    try {
        const response = yield call(testingService.startTesting, action.payload);
        yield put(fetchStartTestingSuccess(response));
    } catch (error: any) {
        const message = error.response?.data?.error || 'Start testing failed';
        const alreadyCompleted = message === 'Initial assessment already completed.';
        yield put(fetchStartTestingFailure(message));
        if (alreadyCompleted) {
            yield put(setIsInitialAssessmentCompleted(true));
        }
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            code: alreadyCompleted ? 'assessment.errors.alreadyCompleted' : 'assessment.errors.startFailed',
            message,
            severity: SEVERITY_NOTIFICATION.ERROR,
        });
        emitter.emit(AppEvent.NAVIGATE, {
            to: -1,
        });
    }
}

function* submitQuestionTestingWorker(action: PayloadAction<ISubmitQuestionTesting>): SagaIterator {
    try {
        const currentQuestion = yield select((state) => state.testing.currentQuestion);
        const response = yield call(testingService.submitQuestion, action.payload);
        yield put(fetchSubmitQuestionTestingSuccess(response));
        yield put(setCurrentQuestion(currentQuestion + 1));
    } catch (error: any) {
        yield put(fetchSubmitQuestionTestingFailure(error.response?.data?.error || 'Submit question testing failed'));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: error.response?.data?.error || 'Submit question testing failed',
            severity: SEVERITY_NOTIFICATION.ERROR,
        });
    }
}

function* submitTestingWorker(action: PayloadAction<ISubmitTesting>): SagaIterator {
    try {
        const token = yield select((state) => state.testing.token);
        // The page's `activities` is from its last render, so the answer to the final question is
        // not in it yet; the store already has every answer.
        const activities = yield select((state) => state.testing.activities);
        if (!activities?.length) {
            // Nothing answered (e.g. the timer ran out while the questions failed to load):
            // submitting would end the assessment with no result.
            yield put(fetchSubmitTestingFailure('No answered questions to submit.'));
            emitter.emit(AppEvent.SHOW_NOTIFICATION, {
                code: 'assessment.errors.noAnswers',
                message: 'No answered questions to submit.',
                severity: SEVERITY_NOTIFICATION.ERROR,
            });
            emitter.emit(AppEvent.NAVIGATE, {
                to: -1,
            });
            return;
        }
        yield call(testingService.submitTesting, { ...action.payload, activities });
        yield put(fetchSubmitTestingSuccess());
        yield put(clearSurvey());
        yield put(setIsInitialAssessmentCompleted(true));
        emitter.emit(AppEvent.NAVIGATE, {
            to: '/testing-result?token=' + token,
        });
    } catch (error: any) {
        yield put(fetchSubmitTestingFailure(error.response?.data?.error || 'Submit testing failed'));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: error.response?.data?.error || 'Submit testing failed',
            severity: SEVERITY_NOTIFICATION.ERROR,
        });
    }
}

function* getResultTestingWorker(action: PayloadAction<string>): SagaIterator {
    try {
        const response = yield call(testingService.getResultTesting, action.payload);
        yield put(fetchResultTestingSuccess(response));
    } catch (error: any) {
        yield put(fetchResultTestingFailure(error.response?.data?.error || 'Get result testing failed'));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: error.response?.data?.error || 'Get result testing failed',
            severity: SEVERITY_NOTIFICATION.ERROR,
        });
    }
}

function* getAssessmentListWorker(action: PayloadAction<IGetListAssessmentParams>): SagaIterator {
    try {
        const response = yield call(testingService.getListHistoryTest, action.payload);
        yield put(fetchHistoryListSuccess(response));
    } catch (error: any) {
        yield put(fetchHistoryListFailure(error.response?.data?.message || 'Get history list failed'));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: error.response?.data?.message || 'Get history list failed',
            severity: SEVERITY_NOTIFICATION.ERROR,
        });
    }
}

function* getAssessmentDetailsWorker(action: PayloadAction<string | number>): SagaIterator {
    try {
        const response = yield call(testingService.getDetailsHistoryTest, action.payload);
        yield put(fetchHistoryDetailsSuccess(response));
    } catch (error: any) {
        yield put(fetchHistoryDetailsFailure(error.response?.data?.message || 'Get history details failed'));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: error.response?.data?.message || 'Get history details failed',
            severity: SEVERITY_NOTIFICATION.ERROR,
        });
    }
}

function* createTestingLessonWorker(action: PayloadAction<'full_test' | 'quick_test'>): SagaIterator {
    try {
        const response = yield call(testingService.createTestingLesson, action.payload);
        yield put(fetchCreateTestingLessonSuccess(response));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: response.message || 'Testing lesson created successfully',
            severity: SEVERITY_NOTIFICATION.SUCCESS,
        });
    } catch (error: any) {
        yield put(fetchCreateTestingLessonFailure(error.response?.data?.message || 'Create testing lesson failed'));
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: error.response?.data?.message || 'Create testing lesson failed',
            severity: SEVERITY_NOTIFICATION.ERROR,
        });
    }
}

function* watchTesting(): SagaIterator {
    yield takeLatest(fetchCreateSurveyRequest.type, createSurveyWorker);
    yield takeLatest(fetchStartTestingRequest.type, startTestingWorker);
    yield takeLatest(fetchSubmitQuestionTestingRequest.type, submitQuestionTestingWorker);
    yield takeLatest(fetchSubmitTestingRequest.type, submitTestingWorker);
    yield takeLatest(fetchResultTestingRequest.type, getResultTestingWorker);
    yield takeLatest(fetchHistoryListRequest.type, getAssessmentListWorker);
    yield takeLatest(fetchHistoryDetailsRequest.type, getAssessmentDetailsWorker);
    yield takeLatest(fetchCreateTestingLessonRequest.type, createTestingLessonWorker);
}

export default watchTesting;
